<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { useSettingsStore } from '@/stores/useSettingsStore'
import { useTranslationService } from '@/utils/translationService'
import { translateLanguageNames, type LanguageOption } from '@/utils/languageNameTranslator'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { t } = useI18n()
const { smAndDown } = useDisplay()
const settings = useSettingsStore()
const translationService = useTranslationService()

const search = ref('')
const searchInputRef = ref<HTMLInputElement | null>(null)

const topLanguages: LanguageOption[] = [
  { code: 'en', name: 'English' },
  { code: 'zh', name: 'Chinese (Mainland)' },
  { code: 'zh-tw', name: 'Chinese (Taiwan)' },
  { code: 'zh-hk', name: 'Chinese (Hong Kong)' },
  { code: 'hi', name: 'Hindi' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'ar', name: 'Arabic (Standard)' },
  { code: 'bn', name: 'Bengali' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
]

const allLanguages: LanguageOption[] = [
  { code: 'af', name: 'Afrikaans' },
  { code: 'sq', name: 'Albanian' },
  { code: 'am', name: 'Amharic' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hy', name: 'Armenian' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'bn', name: 'Bengali' },
  { code: 'bs', name: 'Bosnian' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'ca', name: 'Catalan' },
  { code: 'zh', name: 'Chinese (Mainland)' },
  { code: 'zh-tw', name: 'Chinese (Taiwan)' },
  { code: 'zh-hk', name: 'Chinese (Hong Kong)' },
  { code: 'hr', name: 'Croatian' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'prs', name: 'Dari' },
  { code: 'nl', name: 'Dutch' },
  { code: 'en', name: 'English' },
  { code: 'et', name: 'Estonian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'fr', name: 'French' },
  { code: 'fr-CA', name: 'French (Canada)' },
  { code: 'ka', name: 'Georgian' },
  { code: 'de', name: 'German' },
  { code: 'el', name: 'Greek' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'ht', name: 'Haitian Creole' },
  { code: 'ha', name: 'Hausa' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'is', name: 'Icelandic' },
  { code: 'id', name: 'Indonesian' },
  { code: 'ga', name: 'Irish' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'kn', name: 'Kannada' },
  { code: 'kk', name: 'Kazakh' },
  { code: 'ko', name: 'Korean' },
  { code: 'lv', name: 'Latvian' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'mk', name: 'Macedonian' },
  { code: 'ms', name: 'Malay' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'mt', name: 'Maltese' },
  { code: 'mr', name: 'Marathi' },
  { code: 'mn', name: 'Mongolian' },
  { code: 'no', name: 'Norwegian' },
  { code: 'ps', name: 'Pashto' },
  { code: 'fa', name: 'Persian (Farsi)' },
  { code: 'pl', name: 'Polish' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'sr', name: 'Serbian' },
  { code: 'si', name: 'Sinhala' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'so', name: 'Somali' },
  { code: 'es', name: 'Spanish' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'sw', name: 'Swahili' },
  { code: 'sv', name: 'Swedish' },
  { code: 'tl', name: 'Tagalog' },
  { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' },
  { code: 'th', name: 'Thai' },
  { code: 'tr', name: 'Turkish' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'ur', name: 'Urdu' },
  { code: 'uz', name: 'Uzbek' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'cy', name: 'Welsh' },
]

const sortedAll = [...allLanguages].sort((a, b) => a.name.localeCompare(b.name))

const translatedTop = ref<LanguageOption[]>([...topLanguages])
const translatedAll = ref<LanguageOption[]>([...sortedAll])

const refreshTranslatedNames = async (targetLanguage: string) => {
  try {
    translatedTop.value = translateLanguageNames(topLanguages, targetLanguage)
    translatedAll.value = translateLanguageNames(sortedAll, targetLanguage)
  } catch {
    translatedTop.value = [...topLanguages]
    translatedAll.value = [...sortedAll]
  }
}

const filteredTop = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return translatedTop.value
  return translatedTop.value.filter(l =>
    l.name.toLowerCase().includes(q) || l.code.toLowerCase().includes(q)
  )
})

const filteredAll = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return translatedAll.value
  return translatedAll.value.filter(l =>
    l.name.toLowerCase().includes(q) || l.code.toLowerCase().includes(q)
  )
})

const selectLanguage = async (code: string) => {
  if (!code || code === settings.language) {
    emit('update:modelValue', false)
    return
  }
  settings.language = code
  await translationService?.changeLanguage(code)
  await refreshTranslatedNames(code)
  emit('update:modelValue', false)
}

watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      search.value = ''
      // Focus before the async translation refresh. iOS WKWebView is
      // strict about programmatic focus() — the closer the call is to
      // the synchronous open gesture, the better the chance the system
      // keyboard actually opens. Translation refresh still happens; it
      // just doesn't block the focus attempt.
      await nextTick()
      searchInputRef.value?.focus()
      await refreshTranslatedNames(settings.language)
    }
  }
)
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="(v: boolean) => emit('update:modelValue', v)"
    :fullscreen="smAndDown"
    :max-width="smAndDown ? undefined : 420"
    :transition="smAndDown ? 'dialog-bottom-transition' : 'dialog-transition'"
    scrim="rgba(0,0,0,0.6)"
  >
    <div class="lang-picker" :class="{ 'lang-picker--floating': !smAndDown }">
      <header class="lang-picker__header">
        <h2 class="lang-picker__title">{{ t('common.language') }}</h2>
        <v-btn
          icon="mdi-close"
          variant="text"
          :aria-label="t('common.close')"
          @click="emit('update:modelValue', false)"
        />
      </header>

      <div class="lang-picker__search">
        <div class="lang-picker__search-wrap">
          <v-icon
            icon="mdi-magnify"
            size="20"
            class="lang-picker__search-icon"
            aria-hidden="true"
          />
          <input
            ref="searchInputRef"
            v-model="search"
            type="text"
            class="lang-picker__search-input"
            :placeholder="t('language_picker.search_placeholder')"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
          />
        </div>
      </div>

      <div class="lang-picker__list">
        <template v-if="filteredTop.length">
          <div class="lang-picker__section-label">
            {{ t('language_picker.popular') }}
          </div>
          <button
            v-for="lang in filteredTop"
            :key="`top-${lang.code}`"
            type="button"
            class="lang-picker__item"
            :class="{ 'lang-picker__item--active': lang.code === settings.language }"
            @click="selectLanguage(lang.code)"
          >
            <span class="lang-picker__name">{{ lang.name }}</span>
            <v-icon
              v-if="lang.code === settings.language"
              icon="mdi-check"
              size="20"
              class="lang-picker__check"
            />
          </button>
        </template>

        <template v-if="filteredAll.length">
          <div class="lang-picker__section-label">
            {{ t('language_picker.all') }}
          </div>
          <button
            v-for="lang in filteredAll"
            :key="`all-${lang.code}`"
            type="button"
            class="lang-picker__item"
            :class="{ 'lang-picker__item--active': lang.code === settings.language }"
            @click="selectLanguage(lang.code)"
          >
            <span class="lang-picker__name">{{ lang.name }}</span>
            <v-icon
              v-if="lang.code === settings.language"
              icon="mdi-check"
              size="20"
              class="lang-picker__check"
            />
          </button>
        </template>

        <div v-if="!filteredTop.length && !filteredAll.length" class="lang-picker__empty">
          {{ t('language_picker.no_matches') }}
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<style scoped>
.lang-picker {
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  padding-top: var(--safe-area-top);
}

/* Floating-dialog variant for desktop: cap height so the list scrolls
   inside the dialog rather than growing off-screen, and round the
   corners to match other Vuetify dialogs. */
.lang-picker--floating {
  min-height: 0;
  max-height: min(80vh, 640px);
  border-radius: 12px;
  overflow: hidden;
}

.lang-picker__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.lang-picker__title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

.lang-picker__search {
  padding: 10px 16px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.lang-picker__search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.lang-picker__search-icon {
  position: absolute;
  left: 12px;
  color: rgba(var(--v-theme-on-surface), 0.55);
  pointer-events: none;
}

.lang-picker__search-input {
  width: 100%;
  /* Left padding makes room for the magnifying-glass icon so users
   * immediately read the field as a search box rather than a generic
   * input. Font stays at 16px to prevent iOS Safari's auto-zoom on
   * focus — anything smaller zooms the viewport. */
  padding: 9px 12px 9px 38px;
  border-radius: 8px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-on-surface), 0.04);
  color: inherit;
  font-size: 16px;
}

.lang-picker__search-input:focus {
  outline: none;
  border-color: rgb(var(--v-theme-primary));
}

.lang-picker__list {
  flex: 1;
  overflow-y: auto;
  padding-bottom: env(safe-area-inset-bottom);
}

.lang-picker__section-label {
  padding: 10px 16px 4px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.lang-picker__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  /* Tighter vertical rhythm so more of the 75-language list is in
   * view at once — denser list surfaces the header + search box as a
   * clear zone at the top rather than blending into a single tall
   * column. 42px min-height stays within iOS HIG tap-target guidance. */
  padding: 10px 16px;
  background: transparent;
  border: 0;
  color: inherit;
  font-size: 0.92rem;
  text-align: inherit;
  cursor: pointer;
  min-height: 42px;
}

.lang-picker__item:hover,
.lang-picker__item:focus-visible {
  background: rgba(var(--v-theme-on-surface), 0.06);
  outline: none;
}

.lang-picker__item--active {
  background: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

.lang-picker__name {
  flex: 1;
}

.lang-picker__check {
  flex-shrink: 0;
  margin-left: 12px;
}

.lang-picker__empty {
  padding: 32px 16px;
  text-align: center;
  color: rgba(var(--v-theme-on-surface), 0.6);
}
</style>
